import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

const AttachmentContext = createContext(null)

const LAYOUT_MODE_KEY = 'denali-ai-layout-mode'
const VALID_LAYOUTS = ['sidebar', 'split', 'focus', 'full']

export const LAYOUT_PRESETS = {
  sidebar: { chatFlex: 3, panelFlex: 1 },
  split: { chatFlex: 1, panelFlex: 1 },
  focus: { chatFlex: 1, panelFlex: 3 },
  full: { chatFlex: 0, panelFlex: 1 }
}

function loadLayoutMode () {
  const stored = localStorageHelper.tryLoad(LAYOUT_MODE_KEY)
  return VALID_LAYOUTS.includes(stored) ? stored : 'sidebar'
}

export function AttachmentProvider ({ children, threadId, focus }) {
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [layoutMode, setLayoutModeState] = useState(loadLayoutMode)
  const [customRatio, setCustomRatioState] = useState(null)
  const previousThreadIdRef = useRef(threadId)

  useEffect(() => {
    if (threadId !== previousThreadIdRef.current && isOpen) {
      setIsOpen(false)
      setSelectedAttachment(null)
    }
    previousThreadIdRef.current = threadId
  }, [threadId, isOpen])

  const openAttachment = useCallback((attachment) => {
    setSelectedAttachment(attachment)
    setIsOpen(true)
  }, [])

  const closeAttachment = useCallback(() => {
    setIsOpen(false)
    setTimeout(() => setSelectedAttachment(null), 300)
  }, [])

  const setLayoutMode = useCallback((mode) => {
    if (!VALID_LAYOUTS.includes(mode)) return
    setLayoutModeState(mode)
    setCustomRatioState(null)
    localStorageHelper.tryStore(LAYOUT_MODE_KEY, mode)
  }, [])

  const setCustomRatio = useCallback((ratio) => {
    setCustomRatioState(ratio)
  }, [])

  const effectivePreset = customRatio || LAYOUT_PRESETS[layoutMode] || LAYOUT_PRESETS.sidebar

  const value = useMemo(() => ({
    selectedAttachment,
    isOpen,
    openAttachment,
    closeAttachment,
    focus,
    layoutMode,
    setLayoutMode,
    customRatio,
    setCustomRatio,
    effectivePreset
  }), [selectedAttachment, isOpen, openAttachment, closeAttachment, focus, layoutMode, setLayoutMode, customRatio, setCustomRatio, effectivePreset])

  return (
    <AttachmentContext.Provider value={value}>
      {children}
    </AttachmentContext.Provider>
  )
}

AttachmentProvider.propTypes = {
  children: PropTypes.node,
  threadId: PropTypes.string,
  focus: PropTypes.object
}

export function useAttachment () {
  const context = useContext(AttachmentContext)
  if (!context) {
    throw new Error('useAttachment must be used within an AttachmentProvider')
  }
  return context
}
